"use client";

import Link from "next/link";
import { ArrowRight, PiggyBank, TrendingUp, Receipt, Target, BookOpen, Table2 } from "lucide-react";

import { AnimatedSectionDescription } from "@/components/app-shell/AnimatedSectionDescription";
import { useBudgetData } from "@/components/app-shell/useBudgetData";
import { Button } from "@/components/ui/button";

const features = [
  {
    href: "/savings",
    label: "Track Savings",
    description: "Log each deposit and watch your savings totals build over time.",
    icon: PiggyBank,
    iconClass: "bg-sky-500/10 text-sky-500",
  },
  {
    href: "/investments",
    label: "Investments",
    description: "Monitor every instrument you have recorded in Convex.",
    icon: TrendingUp,
    iconClass: "bg-emerald-500/10 text-emerald-600",
  },
  {
    href: "/expenses",
    label: "Expense Tracking",
    description: "Categorize spending and keep your monthly totals visible.",
    icon: Receipt,
    iconClass: "bg-red-500/10 text-red-500",
  },
  {
    href: "/goals",
    label: "Savings Goals",
    description: "Set targets and increment progress without changing your existing backend.",
    icon: Target,
    iconClass: "bg-amber-500/10 text-amber-500",
  },
];

export function HomePage() {
  const { displayName } = useBudgetData();

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-8">
        <h1 className="mb-1 text-card-foreground">Welcome back, {displayName}</h1>
      </div>

      

      <section className="mb-10">
        <div className="mb-5">
          <h2 className="mb-1 text-card-foreground">What you can manage</h2>
          <AnimatedSectionDescription className="text-sm text-muted-foreground" delay={0.08}>
            The new shell splits your current finance data into focused pages instead of the old tabbed dashboard.
          </AnimatedSectionDescription>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {features.map(({ href, label, description, icon: Icon, iconClass }) => (
            <Link key={href} href={href} className="group rounded-2xl border border-border bg-yellow-200 p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl ${iconClass}`}>
                <Icon className="size-4" />
              </div>
              <h3 className="mb-2 text-blue-600 text-sm font-semibold ">{label}</h3>
              <p className="text-sm text-black">{description}</p>
              <div className="mt-4 flex items-center gap-1 text-xs text-sky-500 opacity-0 transition-opacity group-hover:opacity-100">
                Open <ArrowRight className="size-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="space-y-4">
        <section className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-sky-500/20 bg-sky-500/5 p-6 sm:flex-row sm:items-center">
          <div>
            <div className="mb-1 flex items-center gap-2 text-card-foreground">
              <BookOpen className="size-4 text-sky-500" />
              <span className="font-medium">Financial literacy</span>
            </div>
            <AnimatedSectionDescription className="text-sm text-muted-foreground" delay={0.12}>
              Jump into the financial literacy section for practical tips, strategy guides, and recommended reading.
            </AnimatedSectionDescription>
          </div>
          <Button asChild className="rounded-xl bg-sky-500 text-white hover:bg-sky-600">
            <Link href="/literacy">Open Financial Literacy</Link>
          </Button>
        </section>

        <section className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-violet-500/20 bg-violet-500/5 p-6 sm:flex-row sm:items-center">
          <div>
            <div className="mb-1 flex items-center gap-2 text-card-foreground">
              <Table2 className="size-4 text-violet-500" />
              <span className="font-medium">Tables</span>
            </div>
            <AnimatedSectionDescription className="text-sm text-muted-foreground" delay={0.16}>
              Open the tables section to scan your expenses, savings, investments, and goals in one high-contrast view.
            </AnimatedSectionDescription>
          </div>
          <Button asChild className="rounded-xl bg-violet-500 text-white hover:bg-violet-600">
            <Link href="/tables">Open Tables</Link>
          </Button>
        </section>
      </div>
    </div>
  );
}
